import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle, Wrench, Shield, Clock, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Header } from './Header';
import { Footer } from './Footer';
import { WhatsAppButton } from './WhatsAppButton';
import { CallButton } from './CallButton';
import { TopAnnouncementBar } from './TopAnnouncementBar';

export const PorscheRepair = () => {
  const porscheServices = [
    {
      title: "Engine Diagnostics & Repair",
      description: "Advanced Porsche engine diagnostics using specialized PIWIS diagnostic tools",
      icon: Wrench
    },
    {
      title: "Transmission Service",
      description: "Expert Porsche PDK and manual transmission repair and maintenance",
      icon: Wrench
    },
    {
      title: "Brake System Service",
      description: "Complete Porsche brake inspection, PCCB ceramic brake service",
      icon: Shield
    },
    {
      title: "Electrical Systems",
      description: "Porsche electrical diagnostics and PCM system repair",
      icon: Wrench
    },
    {
      title: "Suspension & Steering",
      description: "Porsche PASM suspension repair and wheel alignment",
      icon: Wrench
    },
    {
      title: "Oil Change & Maintenance",
      description: "Porsche-specific oil changes and routine maintenance services",
      icon: Clock
    },
    {
      title: "AC System Repair",
      description: "Porsche air conditioning service and refrigerant recharge",
      icon: Wrench
    },
    {
      title: "Performance Tuning",
      description: "Porsche performance upgrades and ECU tuning services",
      icon: Wrench
    }
  ];

  const porscheModels = [
    "Porsche 911 (all variants)", "Porsche 718 Boxster", "Porsche 718 Cayman",
    "Porsche Cayenne", "Porsche Macan", "Porsche Panamera",
    "Porsche Taycan", "Porsche 911 Turbo", "Porsche 911 GT3",
    "Porsche 911 Carrera", "Porsche 911 Targa", "Porsche Cayman GT4",
    "Porsche Boxster Spyder", "Porsche Cayenne Turbo", "Porsche Macan GTS",
    "Porsche Panamera Turbo", "Porsche 918 Spyder", "Classic Porsche Models"
  ];

  const whyChooseUs = [
    {
      title: "Porsche-Certified Technicians",
      description: "Our mechanics are specially trained and certified for Porsche repairs"
    },
    {
      title: "Genuine Porsche Parts",
      description: "We use OEM and genuine Porsche parts for all repairs"
    },
    {
      title: "PIWIS Diagnostic Tools",
      description: "State-of-the-art Porsche PIWIS diagnostic equipment"
    },
    {
      title: "Performance Expertise",
      description: "Specialists in Porsche performance and sports car maintenance"
    }
  ];

  const openWhatsApp = () => {
    const message = "Hi Think Mechanic, I need Porsche repair service and want to claim my 15% website discount!";
    const whatsappNumber = '+971566671468';
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Porsche Repair Dubai | Porsche Specialists UAE | Think Mechanic</title>
        <meta name="description" content="Premium Porsche repair services in Dubai including diagnostics, engine repair, brake service and maintenance for all Porsche models." />
        <link rel="canonical" href="https://thinkmechanicdxb.com/porsche-repair-dubai" />
        <meta name="keywords" content="Porsche repair Dubai, Porsche service Dubai, Porsche garage Dubai, Porsche workshop Dubai, Porsche mechanic Dubai, Porsche specialist Dubai, Porsche garage Ras Al Khor, Porsche mechanic Ras Al Khor, Porsche garage near me, Porsche mechanic near me, Porsche AC repair Dubai, Porsche oil change Dubai, Porsche brake repair Dubai, Porsche engine repair Dubai, Porsche transmission repair Dubai, Porsche PDK service Dubai, Porsche maintenance Dubai, Porsche diagnostic Dubai, Porsche repair near me, Best Porsche garage Dubai, Porsche service center Dubai, Affordable Porsche repair Dubai, Porsche oil change near me, Porsche brake service Dubai, Porsche PASM repair Dubai, Porsche suspension repair Dubai, Porsche electrical repair Dubai, Porsche battery replacement Dubai, Think Mechanic Porsche, Porsche 911 repair Dubai, Porsche Cayenne repair Dubai, Porsche Macan repair Dubai, Porsche repair Ras Al Khor, Emergency Porsche repair Dubai, Same day Porsche service, Porsche PCM repair Dubai, Porsche check engine light Dubai, Certified Porsche technician Dubai, Independent Porsche garage Dubai, Trusted Porsche mechanic Dubai, Porsche 911 Turbo repair, Porsche GT3 service Dubai, Porsche Cayenne service, Porsche Macan GTS repair, Porsche Panamera service Dubai, Porsche Taycan repair Dubai, Porsche PCCB brake service, Out of warranty Porsche repair, Porsche extended warranty Dubai, Porsche pre-purchase inspection, Used Porsche inspection Dubai, Top Porsche garage Dubai, Porsche service Al Quoz, Porsche repair Sharjah, car AC repair Dubai, auto AC repair near me" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Porsche Repair Dubai | Expert Porsche Service | Think Mechanic" />
        <meta property="og:description" content="Professional Porsche repair in Dubai. Certified technicians, genuine parts, 15+ years experience. All Porsche models serviced." />
        <meta property="og:url" content="https://thinkmechanicdxb.com/porsche-repair-dubai" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Porsche Repair Dubai | Think Mechanic" />
        <meta name="twitter:description" content="Expert Porsche repair and maintenance in Dubai. Certified technicians, genuine parts." />
      </Helmet>

      {/* Top Announcement Bar */}
      <TopAnnouncementBar />

      {/* Same Header as Homepage */}
      <Header />

      {/* Hero Section with Porsche Image Background */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Porsche Background Image - Porsche in professional workshop */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1683484300853-56c54967af2b?w=1920&q=85"
            alt="Porsche Car in Professional Workshop - Think Mechanic Dubai"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center center' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center py-32">
            {/* Porsche Logo Badge */}
            <div className="inline-flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-md border-2 border-white/20 px-6 py-4 rounded-full mb-6">
              <div className="w-16 h-16 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_think-mechanic/artifacts/1l82b2xy_image.png"
                  alt="Porsche Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-gray-900 font-bold text-xl">Porsche</span>
                <span className="text-white/80 text-sm font-semibold">Repair Specialists</span>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Expert Porsche Repair in
              <span className="text-red-600"> Dubai</span>
            </h1>

            {/* Subheading */}
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Specialized Porsche repair and maintenance in Ras Al Khor. Trust certified technicians with your sports car.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={openWhatsApp}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-red-600/50 group"
              >
                Book Porsche Service & Get 15% OFF
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-white/90 text-sm">✨ Special discount for website visitors</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">15+</div>
                <div className="text-gray-300 text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">400+</div>
                <div className="text-gray-300 text-sm">Porsches Serviced</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">100%</div>
                <div className="text-gray-300 text-sm">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Element */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-10"></div>
      </section>

      {/* Porsche Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-3 mb-4">
              Comprehensive Porsche Repair Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From routine maintenance to performance tuning, we handle all Porsche service needs with expertise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {porscheServices.map((service, index) => (
              <Card key={index} className="bg-white border-gray-200 p-6 hover:border-red-600 transition-all duration-300">
                <div className="bg-red-600/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-gray-900 font-bold text-lg mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Think Mechanic for Porsche Repair?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((reason, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="bg-red-600 rounded-full p-2 flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-gray-900" />
                </div>
                <div>
                  <h3 className="text-gray-900 font-bold text-xl mb-2">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Porsche Models We Service */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Porsche Models We Service
            </h2>
            <p className="text-gray-600 text-lg">
              We service all Porsche models, from classic to latest sports cars
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {porscheModels.map((model, index) => (
              <div key={index} className="bg-white border border-gray-200 px-4 py-3 rounded-lg text-center hover:border-red-600 transition-all duration-300">
                <span className="text-gray-900 font-semibold">{model}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600">+ All other Porsche models</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Ready to Service Your Porsche?
          </h2>
          <p className="text-white/90 text-xl mb-8 max-w-2xl mx-auto">
            Book your Porsche service today and get 15% off. Our expert technicians are ready to help!
          </p>
          
          <Button
            onClick={openWhatsApp}
            className="bg-white hover:bg-gray-100 text-red-600 px-10 py-6 rounded-lg text-xl font-bold transition-all duration-200 shadow-2xl hover:scale-105"
          >
            Book Now via WhatsApp
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-900">
            <div className="flex items-center space-x-2">
              <span className="font-semibold">📞 056 667 1468</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-white/30"></div>
            <div>
              <span>📍 Ras Al Khor Industrial Area 1, Dubai</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      {/* Call Button */}
      <CallButton />
      
      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
};
